import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useLang } from "@/i18n";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export const CartSheet = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const { t, tl } = useLang();
  const { format } = useCurrency();
  const navigate = useNavigate();

  const goCheckout = () => { setOpen(false); navigate("/checkout"); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-sheet" className="rounded-none border-l-[#EAE6E1] bg-[#FAF9F6] w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl text-left text-[#1A1F2C]">{t("cart_title")}</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 text-[#6B7280]">
            <ShoppingBag className="w-10 h-10" strokeWidth={1} />
            <p className="text-sm">{t("cart_empty")}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 flex flex-col gap-5">
              {items.map((item) => (
                <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex gap-4">
                  <div className="w-20 h-24 bg-[#F0EAE1] overflow-hidden shrink-0">
                    <img src={item.image} alt={tl(item.name)} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <h4 className="font-serif-display text-lg text-[#1A1F2C] leading-tight">{tl(item.name)}</h4>
                    <span className="text-sm text-[#6B7280] mb-2">{format(item.price)}</span>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-[#EAE6E1]">
                        <button data-testid={`cart-decrease-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity - 1)} className="px-2 py-1.5 hover:bg-[#F0EAE1]">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button data-testid={`cart-increase-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity + 1)} className="px-2 py-1.5 hover:bg-[#F0EAE1]">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button data-testid={`cart-remove-${item.product_id}`} onClick={() => removeItem(item.product_id)} className="text-[#6B7280] hover:text-[#991B1B] transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#EAE6E1] pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-xs tracking-luxe uppercase text-[#6B7280]">{t("cart_subtotal")}</span>
                <span data-testid="cart-subtotal" className="font-serif-display text-2xl text-[#1A1F2C]">{format(subtotal)}</span>
              </div>
              <button
                data-testid="checkout-button"
                onClick={goCheckout}
                className="w-full bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase py-4 hover:bg-[#C5A880] transition-colors duration-300"
              >
                {t("cart_checkout")}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
